import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

/**
 * Holds a reference to the *current page's* tour starter and whether a tour
 * or onboarding modal is currently active (so other overlays like the Review
 * Reminder can defer showing until the tour/onboarding is done).
 */

interface PageTourContextValue {
  /** Start the current page's tour, if one is registered. No-op if none. */
  startCurrentPageTour: () => void;
  /** True when a tour tooltip or the welcome modal is visible. */
  isTourActive: boolean;
  /** Called by usePageTour / WelcomeModal to set the active state. */
  setTourActive: (active: boolean) => void;
}

const PageTourContext = createContext<PageTourContextValue | null>(null);

export const usePageTourContext = (): PageTourContextValue => {
  const ctx = useContext(PageTourContext);
  if (!ctx) {
    return { startCurrentPageTour: () => {}, isTourActive: false, setTourActive: () => {} };
  }
  return ctx;
};

interface PageTourProviderProps {
  children: ReactNode;
}

/**
 * Provider that exposes the current page's tour starter. Pages register their
 * starter by rendering the `<PageTourRegistrar>` returned by `usePageTour`.
 */
export const PageTourProvider = ({ children }: PageTourProviderProps) => {
  const [starter, setStarter] = useState<(() => void) | null>(null);
  const [isTourActive, setIsTourActive] = useState(false);

  const startCurrentPageTour = useCallback(() => {
    starter?.();
  }, [starter]);

  const setTourActive = useCallback((active: boolean) => {
    setIsTourActive(active);
  }, []);

  const value: PageTourContextValue = { startCurrentPageTour, isTourActive, setTourActive };

  // Wrap setStarter so passing a function doesn't get interpreted as a state
  // updater. registerStarter(startTour) should STORE startTour, not call it.
  const registerStarter = useCallback((s: (() => void) | null) => {
    setStarter(() => s);
  }, []);

  return (
    <PageTourContext.Provider value={value}>
      <PageTourRegistrarContext.Provider value={registerStarter}>
        {children}
      </PageTourRegistrarContext.Provider>
    </PageTourContext.Provider>
  );
};

// Internal context used by usePageTour to publish its starter up to the provider.
const PageTourRegistrarContext = createContext<
  ((starter: (() => void) | null) => void) | null
>(null);

export const usePageTourRegistrar = (): ((starter: (() => void) | null) => void) | null =>
  useContext(PageTourRegistrarContext);

export default PageTourProvider;